/**
 * GROUP DEVOTIONALS (F4): plan/series with items (content + verses + target
 * date), mark done/not-done + REFLECTION (sensitive data: only the author and
 * the moderator sync it — isolation is done by the SYNC, not this UI), the
 * MODERATION panel (comment + seen) and an OPT-IN gamification streak (by
 * church AND by account; never penalizes; respects reduced-motion).
 */
import { msg } from "@lingui/core/macro";
import { createMemo, createSignal, For, Show } from "solid-js";
import {
  aggregateGroupProgress,
  buildAgapeEvents,
  computeAgapePlayer,
  deterministicUuid,
  devotionalProgressId,
  devotionalStreak,
  gamificationVisible,
  getDevotionalFollowupsQuery,
  getDevotionalItemsQuery,
  getDevotionalPlansQuery,
  getDevotionalProgressQuery,
  isDone,
  orderDevotionalItems,
  patchRowVerified,
  queryKeys,
} from "@agape/shared";
import {
  type ChurchRecord,
  devotional_followup,
  devotional_item,
  devotional_plan,
  devotional_progress,
  type DevotionalFollowupRecord,
  type DevotionalItemRecord,
  type DevotionalPlanRecord,
  type DevotionalProgressRecord,
  eq,
  type UserRecord,
  user as userTable,
} from "@agape/powersync";
import {
  Award,
  BookOpenText,
  Check,
  Eye,
  Flame,
  ListPlus,
  MessageCircle,
  Plus,
} from "@fleet/icons";

import { useSession } from "@/lib/auth";
import { useI18n } from "@/providers/i18n-provider";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@fleet/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@fleet/ui/switch";
import { showToast } from "@/components/ui/toast";
import { Field, FormDialog, TextArea } from "./form";
import { badgeLabel } from "../badge-labels";
import { newId, nowIso, useDomainQuery, useGrants, useWriter } from "../use-domain";

const pad2 = (n: number) => String(n).padStart(2, "0");

/** LOCAL DAY `YYYY-MM-DD` (the streak lives in the user's time zone). */
function todayLocal(): string {
  const d = new Date();
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

export function DevotionalSection(props: { groupId: string; church: ChurchRecord | undefined }) {
  const { t } = useI18n();
  const write = useWriter();
  const grants = useGrants();
  const { userId } = useSession();

  const plans = useDomainQuery<DevotionalPlanRecord>(
    (uid) => queryKeys.domain.devotionalPlans(uid),
    (db) => getDevotionalPlansQuery(db),
  );
  const items = useDomainQuery<DevotionalItemRecord>(
    (uid) => queryKeys.domain.devotionalItems(uid),
    (db) => getDevotionalItemsQuery(db),
  );
  const progress = useDomainQuery<DevotionalProgressRecord>(
    (uid) => queryKeys.domain.devotionalProgress(uid),
    (db) => getDevotionalProgressQuery(db),
  );
  const followups = useDomainQuery<DevotionalFollowupRecord>(
    (uid) => queryKeys.domain.devotionalFollowups(uid),
    (db) => getDevotionalFollowupsQuery(db),
  );
  const me = useDomainQuery<Pick<UserRecord, "id" | "gamification_opt_in">>(
    (uid) => [...queryKeys.domain.catalogs(), "gamification-me", uid],
    (db) => {
      const uid = userId();
      return uid
        ? db
            .select({ id: userTable.id, gamification_opt_in: userTable.gamification_opt_in })
            .from(userTable)
            .where(eq(userTable.id, uid))
        : null;
    },
  );

  const scope = () => [props.groupId, props.church?.id];
  const canWrite = () => grants.can(scope(), "devotional-tracking", "write");
  const canModerate = () => grants.can(scope(), "devotional-tracking", "manage");

  const myPlans = createMemo(() => (plans.data ?? []).filter((p) => p.group_id === props.groupId));
  const groupItems = createMemo(() =>
    (items.data ?? []).filter((i) => i.group_id === props.groupId),
  );
  const groupProgress = createMemo(() =>
    (progress.data ?? []).filter((p) => p.group_id === props.groupId),
  );
  const myProgress = createMemo(() => groupProgress().filter((p) => p.user_id === userId()));

  // ── Gamification (double opt-in; only adds, never penalizes) ──────────────
  const optedIn = () => Boolean(me.data?.[0]?.gamification_opt_in);
  const churchEnabled = () => Boolean(props.church?.gamification_enabled);
  const showGame = () => gamificationVisible(churchEnabled(), optedIn());
  const streak = createMemo(() => devotionalStreak(myProgress(), userId() ?? "", todayLocal()));
  /** Badges/achievements earned with MY devotional progress (pure projection). */
  const earnedBadges = createMemo(() => {
    const uid = userId();
    if (!uid) {
      return [] as string[];
    }
    const events = buildAgapeEvents({ progress: myProgress(), userId: uid, myPersonIds: [] });
    return computeAgapePlayer(events).badges;
  });
  const toggleOptIn = async (value: boolean) => {
    const uid = userId();
    if (!uid) {
      return;
    }
    // §4.1: `user` is NOT excluded from `patchRowVerified` — `db`/`userId()`
    // become ready before the checkpoint brings the row the server already
    // created, so this UPDATE can run with no row to match. See the note
    // "`user` IS covered" in packages/shared/src/powersync/verified-write.ts.
    try {
      await patchRowVerified(write(), userTable, userTable.id, uid, {
        gamification_opt_in: value ? 1 : 0,
      });
    } catch {
      showToast(t(msg`Couldn't save the change — try again`), "error");
    }
  };

  // ── Create plan ───────────────────────────────────────────────────────────
  const [planOpen, setPlanOpen] = createSignal(false);
  const [planTitle, setPlanTitle] = createSignal("");
  const [planDescription, setPlanDescription] = createSignal("");
  const addPlan = async () => {
    if (!planTitle().trim()) {
      return;
    }
    await write()
      .insert(devotional_plan)
      .values({
        id: newId(),
        church_id: props.church?.id,
        group_id: props.groupId,
        title: planTitle().trim(),
        description: planDescription().trim() || null,
        created_at: nowIso(),
        updated_at: nowIso(),
      });
    setPlanOpen(false);
    setPlanTitle("");
    setPlanDescription("");
    showToast(t(msg`Devotional plan created`), "success");
  };

  // ── Add item ──────────────────────────────────────────────────────────────
  const [itemOpen, setItemOpen] = createSignal(false);
  const [itemPlan, setItemPlan] = createSignal("");
  const [itemTitle, setItemTitle] = createSignal("");
  const [itemVerses, setItemVerses] = createSignal("");
  const [itemContent, setItemContent] = createSignal("");
  const [itemDate, setItemDate] = createSignal("");
  const addItem = async () => {
    if (!itemTitle().trim() || !itemPlan()) {
      return;
    }
    const planItems = groupItems().filter((i) => i.plan_id === itemPlan());
    await write()
      .insert(devotional_item)
      .values({
        id: newId(),
        church_id: props.church?.id,
        group_id: props.groupId,
        plan_id: itemPlan(),
        title: itemTitle().trim(),
        verses: itemVerses().trim() || null,
        content: itemContent().trim() || null,
        target_date: itemDate() || null,
        sort_order: planItems.length + 1,
        created_at: nowIso(),
        updated_at: nowIso(),
      });
    setItemOpen(false);
    setItemTitle("");
    setItemVerses("");
    setItemContent("");
    setItemDate("");
    showToast(t(msg`Devotional added to the plan`), "success");
  };

  // ── Mark done / reflection (own row, deterministic ID) ────────────────────
  const mineFor = (itemId: string) =>
    myProgress().find((p) => p.id === devotionalProgressId(itemId, userId() ?? ""));
  const toggleDone = async (item: DevotionalItemRecord) => {
    const uid = userId();
    if (!uid) {
      return;
    }
    const existing = mineFor(item.id);
    if (existing) {
      const nowDone = !isDone(existing);
      try {
        await patchRowVerified(write(), devotional_progress, devotional_progress.id, existing.id, {
          done: nowDone ? 1 : 0,
          done_on: nowDone ? todayLocal() : null,
        });
      } catch {
        showToast(t(msg`Couldn't save the change — try again`), "error");
      }
    } else {
      await write()
        .insert(devotional_progress)
        .values({
          id: devotionalProgressId(item.id, uid),
          church_id: item.church_id,
          group_id: props.groupId,
          plan_id: item.plan_id,
          item_id: item.id,
          user_id: uid,
          done: 1,
          done_on: todayLocal(),
          created_at: nowIso(),
          updated_at: nowIso(),
        });
    }
  };
  const [reflectionFor, setReflectionFor] = createSignal<string | null>(null);
  const [reflectionText, setReflectionText] = createSignal("");
  const openReflection = (item: DevotionalItemRecord) => {
    setReflectionText(mineFor(item.id)?.reflection ?? "");
    setReflectionFor(item.id);
  };
  const saveReflection = async () => {
    const uid = userId();
    const itemId = reflectionFor();
    if (!uid || !itemId) {
      return;
    }
    const item = groupItems().find((i) => i.id === itemId);
    const existing = mineFor(itemId);
    const text = reflectionText().trim() || null;
    if (existing) {
      await patchRowVerified(write(), devotional_progress, devotional_progress.id, existing.id, {
        reflection: text,
      });
    } else if (item) {
      await write()
        .insert(devotional_progress)
        .values({
          id: devotionalProgressId(itemId, uid),
          church_id: item.church_id,
          group_id: props.groupId,
          plan_id: item.plan_id,
          item_id: itemId,
          user_id: uid,
          done: 0,
          reflection: text,
          created_at: nowIso(),
          updated_at: nowIso(),
        });
    }
    setReflectionFor(null);
    showToast(t(msg`Reflection saved`), "success");
  };

  // ── Moderation: follow-up (comment + seen) ───────────────────────────────
  const followupId = (progressId: string) =>
    // oxlint-disable-next-line lingui/no-unlocalized-strings -- internal key, not UI
    deterministicUuid(`devotional_followup:${progressId}:${userId() ?? ""}`);
  const followupFor = (progressId: string) =>
    (followups.data ?? []).find((f) => f.id === followupId(progressId));
  const myFollowupOn = (progressId: string) =>
    (followups.data ?? []).find(
      (f) => f.progress_id === progressId && f.author_user_id === userId(),
    );
  const [commentFor, setCommentFor] = createSignal<DevotionalProgressRecord | null>(null);
  const [commentText, setCommentText] = createSignal("");
  const upsertFollowup = async (
    row: DevotionalProgressRecord,
    patch: { comment?: string | null; seen?: boolean },
  ) => {
    const existing = followupFor(row.id);
    if (existing) {
      await patchRowVerified(write(), devotional_followup, devotional_followup.id, existing.id, {
        ...(patch.comment === undefined ? {} : { comment: patch.comment }),
        ...(patch.seen ? { seen_at: nowIso() } : {}),
      });
    } else {
      await write()
        .insert(devotional_followup)
        .values({
          id: followupId(row.id),
          church_id: row.church_id,
          group_id: row.group_id,
          progress_id: row.id,
          comment: patch.comment ?? null,
          seen_at: patch.seen ? nowIso() : null,
          created_at: nowIso(),
          updated_at: nowIso(),
        });
    }
  };
  const saveComment = async () => {
    const row = commentFor();
    if (!row) {
      return;
    }
    await upsertFollowup(row, { comment: commentText().trim() || null, seen: true });
    setCommentFor(null);
    setCommentText("");
    showToast(t(msg`Follow-up saved`), "success");
  };

  const moderation = createMemo(() => {
    const byPlan = new Map<string, ReturnType<typeof aggregateGroupProgress>>();
    for (const plan of myPlans()) {
      byPlan.set(plan.id, aggregateGroupProgress(groupItems(), groupProgress(), plan.id));
    }
    return byPlan;
  });
  const itemTitleOf = (id: string | null) =>
    groupItems().find((i) => i.id === id)?.title ?? id ?? "";

  return (
    <section class="space-y-2" data-testid="devotional-section">
      <div class="flex items-center justify-between">
        <h3 class="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          {t(msg`Devotionals`)}
        </h3>
        <div class="flex items-center gap-2">
          <Show when={showGame() && streak().current > 0}>
            <span
              class="inline-flex items-center gap-1 rounded-full bg-accent px-2.5 py-1 text-xs font-semibold text-accent-foreground motion-safe:animate-in motion-safe:fade-in"
              data-testid="devotional-streak"
              data-streak={streak().current}
            >
              <Flame size={13} aria-hidden="true" />
              {(() => {
                const days = streak().current;
                return t(msg`${days} day streak`);
              })()}
            </span>
          </Show>
          <Show when={showGame() && earnedBadges().length > 0}>
            <div class="flex flex-wrap items-center gap-1" data-testid="gamification-badges">
              <For each={earnedBadges()}>
                {(badge) => (
                  <span
                    class="inline-flex items-center gap-1 rounded-full bg-primary/15 px-2.5 py-1 text-xs font-semibold text-primary motion-safe:animate-in motion-safe:fade-in"
                    data-testid="gamification-badge"
                    data-badge={badge}
                    title={badgeLabel(t, badge)}
                  >
                    <Award size={13} aria-hidden="true" />
                    {badgeLabel(t, badge)}
                  </span>
                )}
              </For>
            </div>
          </Show>
          <Show when={canWrite()}>
            <Button
              size="sm"
              variant="ghost"
              data-testid="devotional-plan-create"
              onClick={() => setPlanOpen(true)}
            >
              <BookOpenText size={14} /> {t(msg`New plan`)}
            </Button>
          </Show>
        </div>
      </div>

      {/* Opt-in personal, visible solo si la IGLESIA habilitó el juego. */}
      <Show when={churchEnabled()}>
        <div class="flex items-center justify-between rounded-md border border-border px-3 py-2">
          <span class="text-sm">{t(msg`Join streaks & recognition (optional)`)}</span>
          <Switch
            checked={optedIn()}
            onCheckedChange={(v) => void toggleOptIn(v)}
            aria-label={t(msg`Join streaks & recognition`)}
            data-testid="gamification-opt-in"
          />
        </div>
      </Show>

      <Show
        when={myPlans().length > 0}
        fallback={
          <p class="text-sm text-muted-foreground" data-testid="devotional-empty">
            {t(msg`No devotional plans yet.`)}
          </p>
        }
      >
        <For each={myPlans()}>
          {(plan) => {
            const planItems = createMemo(() =>
              orderDevotionalItems(groupItems().filter((i) => i.plan_id === plan.id)),
            );
            return (
              <Card data-testid="devotional-plan-item">
                <CardContent class="space-y-2 py-3">
                  <div class="flex items-center justify-between gap-2">
                    <div class="min-w-0">
                      <p class="truncate text-sm font-medium">{plan.title}</p>
                      <Show when={plan.description}>
                        <p class="truncate text-xs text-muted-foreground">{plan.description}</p>
                      </Show>
                    </div>
                    <Show when={canWrite()}>
                      <Button
                        size="sm"
                        variant="ghost"
                        data-testid="devotional-item-add"
                        onClick={() => {
                          setItemPlan(plan.id);
                          setItemOpen(true);
                        }}
                      >
                        <ListPlus size={14} /> {t(msg`Add devotional`)}
                      </Button>
                    </Show>
                  </div>

                  <For each={planItems()}>
                    {(item) => {
                      const mine = () => mineFor(item.id);
                      return (
                        <div
                          class="space-y-1 rounded-md border border-border/60 px-3 py-2"
                          data-testid="devotional-item"
                          data-item-id={item.id}
                          data-done={isDone(mine() ?? { done: 0 }) ? "true" : "false"}
                        >
                          <div class="flex items-center justify-between gap-2">
                            <label class="flex min-w-0 cursor-pointer items-center gap-2 text-sm">
                              <Show when={canWrite()}>
                                <Checkbox
                                  checked={isDone(mine() ?? { done: 0 })}
                                  onChange={() => void toggleDone(item)}
                                  aria-label={item.title ?? ""}
                                  data-testid="devotional-done-toggle"
                                />
                              </Show>
                              <span class="min-w-0 truncate font-medium">{item.title}</span>
                            </label>
                            <span class="shrink-0 text-xs text-muted-foreground">
                              {[item.verses, item.target_date].filter(Boolean).join(" · ")}
                            </span>
                          </div>
                          <Show when={item.content}>
                            <p class="text-xs text-muted-foreground">{item.content}</p>
                          </Show>
                          <Show when={canWrite()}>
                            <div class="flex items-center gap-2">
                              <Button
                                size="sm"
                                variant="ghost"
                                data-testid="devotional-reflection-open"
                                onClick={() => openReflection(item)}
                              >
                                <MessageCircle size={13} />
                                {mine()?.reflection
                                  ? t(msg`Edit reflection`)
                                  : t(msg`Write reflection`)}
                              </Button>
                              <Show when={mine() && myFollowupOn(mine()!.id)}>
                                {/* Seguimiento del moderador sobre MI reflexión. */}
                                <span
                                  class="inline-flex items-center gap-1 text-xs text-muted-foreground"
                                  data-testid="devotional-followup-received"
                                >
                                  <Eye size={12} aria-hidden="true" />
                                  {myFollowupOn(mine()!.id)?.comment ?? t(msg`Seen by moderator`)}
                                </span>
                              </Show>
                            </div>
                          </Show>
                        </div>
                      );
                    }}
                  </For>

                  {/* ── Panel del MODERADOR: reflexiones del grupo ─────────── */}
                  <Show when={canModerate()}>
                    <div
                      class="space-y-1.5 border-t border-border pt-2"
                      data-testid="devotional-moderation"
                    >
                      <p class="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                        {t(msg`Member follow-up`)}
                      </p>
                      <Show
                        when={(moderation().get(plan.id)?.perMember.length ?? 0) > 0}
                        fallback={
                          <p class="text-xs text-muted-foreground">
                            {t(msg`No member progress yet.`)}
                          </p>
                        }
                      >
                        <For each={moderation().get(plan.id)?.perMember ?? []}>
                          {(member) => (
                            <div
                              class="rounded-md bg-accent/10 px-2 py-1.5 text-xs"
                              data-testid="devotional-member-row"
                              data-member={member.userId}
                            >
                              <p class="font-medium">
                                {member.userName ?? member.userId} · {member.doneCount}/
                                {moderation().get(plan.id)?.totalItems ?? 0}
                              </p>
                              <For
                                each={groupProgress().filter(
                                  (p) => p.user_id === member.userId && p.plan_id === plan.id,
                                )}
                              >
                                {(row) => (
                                  <div class="mt-1 flex items-start justify-between gap-2">
                                    <p class="min-w-0">
                                      <span class="text-muted-foreground">
                                        {itemTitleOf(row.item_id)}:
                                      </span>{" "}
                                      <Show
                                        when={row.reflection}
                                        fallback={
                                          <span class="italic text-muted-foreground">
                                            {isDone(row)
                                              ? t(msg`done, no reflection`)
                                              : t(msg`not done yet`)}
                                          </span>
                                        }
                                      >
                                        <span data-testid="devotional-reflection-text">
                                          {row.reflection}
                                        </span>
                                      </Show>
                                    </p>
                                    <div class="flex shrink-0 items-center gap-1">
                                      <Show when={followupFor(row.id)?.seen_at}>
                                        <Check
                                          size={12}
                                          aria-label={t(msg`Seen`)}
                                          data-testid="devotional-followup-seen-mark"
                                        />
                                      </Show>
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        data-testid="devotional-followup-open"
                                        onClick={() => {
                                          setCommentText(followupFor(row.id)?.comment ?? "");
                                          setCommentFor(row);
                                        }}
                                      >
                                        {t(msg`Follow up`)}
                                      </Button>
                                    </div>
                                  </div>
                                )}
                              </For>
                            </div>
                          )}
                        </For>
                      </Show>
                    </div>
                  </Show>
                </CardContent>
              </Card>
            );
          }}
        </For>
      </Show>

      {/* ── Diálogos ─────────────────────────────────────────────────────── */}
      <FormDialog
        open={planOpen()}
        onOpenChange={setPlanOpen}
        title={t(msg`New devotional plan`)}
        icon={<BookOpenText size={20} />}
        submitTestId="devotional-plan-submit"
        disabled={!planTitle().trim()}
        onSubmit={addPlan}
      >
        <Field
          id="dp-title"
          label={t(msg`Title`)}
          value={planTitle()}
          onInput={setPlanTitle}
          testId="devotional-plan-title-input"
        />
        <Field
          id="dp-description"
          label={t(msg`Description`)}
          value={planDescription()}
          onInput={setPlanDescription}
        />
      </FormDialog>

      <FormDialog
        open={itemOpen()}
        onOpenChange={setItemOpen}
        title={t(msg`Add devotional`)}
        icon={<Plus size={20} />}
        submitTestId="devotional-item-submit"
        disabled={!itemTitle().trim()}
        onSubmit={addItem}
      >
        <Field
          id="di-title"
          label={t(msg`Title`)}
          value={itemTitle()}
          onInput={setItemTitle}
          testId="devotional-item-title-input"
        />
        <div class="grid grid-cols-2 gap-3">
          <Field
            id="di-verses"
            label={t(msg`Verses`)}
            value={itemVerses()}
            onInput={setItemVerses}
            placeholder={t(msg`John 15:1-8`)}
            testId="devotional-item-verses-input"
          />
          <Field
            id="di-date"
            label={t(msg`Target date`)}
            type="date"
            value={itemDate()}
            onInput={setItemDate}
          />
        </div>
        <Field id="di-content" label={t(msg`Content`)}>
          <TextArea
            id="di-content"
            value={itemContent()}
            onInput={setItemContent}
            rows={3}
            testId="devotional-item-content-input"
          />
        </Field>
      </FormDialog>

      <FormDialog
        open={reflectionFor() !== null}
        onOpenChange={(open) => {
          if (!open) {
            setReflectionFor(null);
          }
        }}
        title={t(msg`My reflection`)}
        icon={<MessageCircle size={20} />}
        submitTestId="devotional-reflection-save"
        onSubmit={saveReflection}
      >
        <p class="text-xs text-muted-foreground">
          {t(msg`Only you and your group moderator can read this.`)}
        </p>
        <Field id="dr-text" label={t(msg`Reflection`)}>
          <TextArea
            id="dr-text"
            value={reflectionText()}
            onInput={setReflectionText}
            rows={4}
            testId="devotional-reflection-input"
          />
        </Field>
      </FormDialog>

      <FormDialog
        open={commentFor() !== null}
        onOpenChange={(open) => {
          if (!open) {
            setCommentFor(null);
          }
        }}
        title={t(msg`Follow up`)}
        icon={<Eye size={20} />}
        submitTestId="devotional-followup-save"
        onSubmit={saveComment}
      >
        <p class="text-xs text-muted-foreground">
          {t(
            msg`The member will see your comment on their reflection. Saving also marks it as seen.`,
          )}
        </p>
        <Field id="df-comment" label={t(msg`Comment`)}>
          <TextArea
            id="df-comment"
            value={commentText()}
            onInput={setCommentText}
            rows={3}
            testId="devotional-followup-input"
          />
        </Field>
      </FormDialog>
    </section>
  );
}
